/**
 * Public AI Tutor — parent-facing chat page launched from the weekly digest email.
 * URL: /ai-tutor?tenant=<tid>&topic=<topic>&subject=<subject>&grade=<grade>&student=<name>
 * No login required. Conversation persists per session_id in localStorage.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Sparkles, Send, GraduationCap, RefreshCw, Mic, MicOff, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const makeSessionId = () => `tutor-${Math.random().toString(36).slice(2)}-${Date.now()}`;

export default function AITutorPage() {
  const [sp] = useSearchParams();
  const topic = sp.get('topic') || '';
  const subject = sp.get('subject') || '';
  const grade = sp.get('grade') || '';
  const student = sp.get('student') || '';

  const [sessionId, setSessionId] = useState(() => {
    const key = `ai-tutor-session-${subject}-${topic}`;
    let s = localStorage.getItem(key);
    if (!s) { s = makeSessionId(); localStorage.setItem(key, s); }
    return s;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const loadHistory = async (sid) => {
    try {
      const r = await fetch(`${API_URL}/api/public/ai-tutor/history?session_id=${sid}`);
      if (r.ok) { const data = await r.json(); setMessages(data); }
    } catch {}
  };

  useEffect(() => { loadHistory(sessionId); }, [sessionId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/public/ai-tutor/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text, topic, subject, grade_level: grade, student_name: student }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'AI error'); }
      const data = await r.json();
      setMessages(prev => [...prev, { role: 'assistant', text: data.reply }]);
    } catch (e) {
      toast.error(e.message);
      setMessages(prev => [...prev, { role: 'assistant', text: '⚠️ Sorry — I had a hiccup. Please try again in a moment.' }]);
    } finally {
      setLoading(false);
    }
  };

  const resetChat = () => {
    const key = `ai-tutor-session-${subject}-${topic}`;
    const s = makeSessionId();
    localStorage.setItem(key, s);
    setSessionId(s);
    setMessages([]);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        stream.getTracks().forEach(t => t.stop());
        if (blob.size < 1000) { toast.error('Recording too short. Hold the button longer.'); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append('audio', blob, 'question.webm');
          const r = await fetch(`${API_URL}/api/public/ai-tutor/transcribe`, { method: 'POST', body: fd });
          if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Transcription failed'); }
          const data = await r.json();
          const text = (data.text || '').trim();
          if (!text) { toast.error("Couldn't hear anything — please try again."); return; }
          setInput(prev => (prev ? prev + ' ' : '') + text);
          toast.success('Transcribed');
        } catch (e) { toast.error(e.message); }
        finally { setTranscribing(false); }
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone permission denied or unavailable');
    }
  };

  const stopRecording = () => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const exportPdf = async () => {
    if (messages.length === 0) { toast.info('Start a conversation first'); return; }
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({ session_id: sessionId, topic, subject, student });
      const r = await fetch(`${API_URL}/api/public/ai-tutor/export-pdf?${params}`);
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Export failed'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `study-sheet-${(topic || 'session').replace(/\s+/g,'-').toLowerCase()}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Study sheet downloaded');
    } catch (e) { toast.error(e.message); }
    finally { setExportingPdf(false); }
  };

  const suggested = [
    topic && `Can you explain "${topic}" in simple terms?`,
    subject && `Give me 3 practice questions for ${subject}`,
    `Help me help my child study this evening`,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50" data-testid="ai-tutor-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="mb-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-600 text-white text-xs font-medium shadow-sm">
            <Sparkles className="h-3.5 w-3.5" /> AI Tutor
          </div>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold text-slate-900" data-testid="tutor-heading">
            Your at-home study helper
          </h1>
          {(subject || topic || grade) && (
            <div className="mt-2 flex items-center justify-center gap-1.5 flex-wrap">
              {subject && <Badge variant="outline" className="bg-white">{subject}</Badge>}
              {topic && <Badge variant="outline" className="bg-white">Topic: {topic}</Badge>}
              {grade && <Badge variant="outline" className="bg-white">Grade {grade}</Badge>}
              {student && <Badge variant="outline" className="bg-white"><GraduationCap className="h-3 w-3 mr-1" /> {student}</Badge>}
            </div>
          )}
        </div>

        <Card className="shadow-lg border-indigo-100">
          <CardHeader className="flex flex-row items-center justify-between border-b py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
              <Sparkles className="h-4 w-4 text-indigo-500" /> Conversation
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={exportPdf} disabled={exportingPdf || messages.length === 0} data-testid="tutor-export-pdf-btn" className="text-xs">
                {exportingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileDown className="h-3 w-3 mr-1" />} Save PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={resetChat} data-testid="tutor-reset-btn" className="text-xs">
                <RefreshCw className="h-3 w-3 mr-1" /> Reset
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div ref={scrollRef} className="h-[60vh] overflow-y-auto p-4 space-y-3" data-testid="tutor-messages">
              {messages.length === 0 ? (
                <div className="text-center py-10">
                  <Sparkles className="h-10 w-10 mx-auto text-indigo-300 mb-3" />
                  <p className="text-sm text-slate-600">Hi! Ask me anything about the topic above — I'll explain it step by step and give you a mini-practice question.</p>
                  <div className="mt-4 space-y-2">
                    {suggested.map((s, i) => (
                      <button key={i} onClick={() => setInput(s)} className="block mx-auto text-xs px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition" data-testid={`tutor-suggested-${i}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`tutor-msg-${i}`}>
                    <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-900 rounded-bl-sm'}`}>
                      {m.text}
                    </div>
                  </div>
                ))
              )}
              {loading && (
                <div className="flex justify-start" data-testid="tutor-thinking">
                  <div className="bg-slate-100 text-slate-700 px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-sm inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking…
                  </div>
                </div>
              )}
            </div>
            <div className="border-t p-3 flex gap-2">
              <Button
                onClick={recording ? stopRecording : startRecording}
                variant={recording ? 'destructive' : 'outline'}
                size="icon"
                disabled={loading || transcribing}
                title={recording ? 'Stop recording' : 'Speak your question'}
                data-testid="tutor-mic-btn"
              >
                {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
              <Input
                placeholder={recording ? 'Listening…' : transcribing ? 'Transcribing…' : 'Type your question…'}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                disabled={loading || recording || transcribing}
                data-testid="tutor-input"
              />
              <Button onClick={send} disabled={!input.trim() || loading || recording || transcribing} data-testid="tutor-send-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        <p className="mt-3 text-[11px] text-center text-slate-500">
          AI responses can make mistakes — please double-check important information. Conversations are saved for 30 days to help your child review.
        </p>
      </div>
    </div>
  );
}
